import type { DocumentNode, GraphQLSchema } from 'graphql';
import { visit } from 'graphql';

import { gql } from '@apollo/client';

import type { AuthState } from '@tmapy/auth';
import type { extendedTWC, HasPermission, WFSInfoState } from '@tmapy/config';
import { isVoidMapConfig } from '@tmapy/config';
import type { LocaleMessages } from '@tmapy/intl';
import type {
  ComponentType,
  QueryRouteConfigTWC,
  RouteConfigTWC,
  RouteStateTWC,
} from '@tmapy/types';

import {
  createDocumentFromSchema,
  getDirectives,
  hasOperation,
  prepareServerDocument,
} from 'lib/graphql';

import { createHasPermission } from '../auth/hooks/usePermission';
import type { DictionaryPages } from '../constants';
import { DICTIONARY_PAGES } from '../constants';

import { getRootRoute } from './getRootRoute';

const mapRoutes: ComponentType[] = ['MapInfo', 'Map', 'Panorama'];
const excludedRouteComponents: ComponentType[] = [...mapRoutes, 'Dictionary', 'IDE', 'UserProfile'];
const notExcludedRouteIds = ['dmx.info', 'dmx.map'];

const processGraphQLRouteConfig = (
  route: QueryRouteConfigTWC,
  isCorrectMapConfig: boolean,
  schema: GraphQLSchema,
  hasPermission: HasPermission,
): RouteStateTWC => {
  try {
    const rawDocument = gql(route.query);

    const auth = route.auth ?? false;
    const hasBasePermission = hasOperation(route.query, schema, hasPermission);

    const childrenRouteIds: string[] = [];
    visit(rawDocument, {
      Field: (field) => {
        const directives = getDirectives(field.directives);
        if (directives.create) {
          childrenRouteIds.push(directives.create.route);
        }
      },
    });

    let showMap = !!route.defaultSize;
    visit(prepareServerDocument(rawDocument, schema), {
      Field: (field) => {
        if (field.namedType?.name === 'EWKT') {
          showMap = isCorrectMapConfig;
        }
      },
      VariableDefinition: (node) => {
        const type = node.type.kind === 'NonNullType' ? node.type.type : node.type;
        if (type.kind === 'NamedType' && type.name.value === 'EWKT') {
          showMap = isCorrectMapConfig;
        }
      },
    });

    return {
      ...route,
      showMap,
      hasBasePermission,
      childrenRouteIds,
      auth,
      component: 'RouterQuery',
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to load GraphQL configuration validly for '${route.id}'. ${message}`);
  }
};

const processMapRouteConfig = (route: RouteConfigTWC, showMap: boolean): RouteStateTWC => {
  const component = route.component;
  const auth = route.auth ?? false;
  const hasBasePermission = true;
  if (component && mapRoutes.includes(component)) {
    return { ...route, component, showMap, auth, hasBasePermission };
  }
  return { ...route, component: component ?? 'NotFound', showMap: false, auth, hasBasePermission };
};

const isQueryRouteConfigTWC = (route: RouteConfigTWC): route is QueryRouteConfigTWC => {
  return !!(
    (route.component === 'RouterQuery' || !route.component) &&
    route.query &&
    typeof route.query === 'string'
  );
};

const isRouteConfigured = (
  page: DictionaryPages,
  appId: string,
  messages: LocaleMessages,
): boolean => {
  const candidates = [`${appId}.${page}.content.html`, `${page}.content.html`];
  return candidates.some((key) => key in messages);
};

export const processRoutesConfig = (
  config: extendedTWC,
  schema: GraphQLSchema,
  authState: AuthState,
  lang: string,
  messages: LocaleMessages,
): RouteStateTWC[] => {
  const showMap = !isVoidMapConfig(config.map);
  const hasPermission = createHasPermission(authState.permissions);

  const basename = typeof config.basename === 'object' ? config.basename[lang] : config.basename;
  const routes: RouteStateTWC[] = (config.routes ?? [])
    .map((route) => {
      const path = typeof route.path === 'object' ? route.path[lang] : route.path;

      if (route.component === undefined && !route.query && path !== '') {
        route.query = createDocumentFromSchema({ route, path, basename, schema });
      }
      return route;
    })
    .filter((route) => {
      const path = typeof route.path === 'object' ? route.path[lang] : route.path;

      if (path === '') {
        console.error(
          `[config.routes]: Config includes route with empty path (route id: "${route.id}").`,
        );
        return false;
      }
      if (
        route.component === undefined ||
        !excludedRouteComponents.includes(route.component) ||
        notExcludedRouteIds.includes(route.id)
      ) {
        return true;
      }
      console.error(`[config.routes]: Unsupported route component: "${route.component}".`);
      return false;
    })
    .map((route) =>
      isQueryRouteConfigTWC(route)
        ? processGraphQLRouteConfig(route, showMap, schema, hasPermission)
        : processMapRouteConfig(route, showMap),
    );

  const rootRoute = getRootRoute(routes, lang);
  const auth = !!(config.hasAuthentication ?? (rootRoute ?? routes[0])?.auth);
  const hasBasePermission = true;

  const isAboutRouteConfigured = isRouteConfigured(DICTIONARY_PAGES.ABOUT, config.appId, messages);
  const isBlankRouteConfigured = isRouteConfigured(DICTIONARY_PAGES.BLANK, config.appId, messages);

  if (rootRoute) {
    // TWC router chape jako root path prazdny retezec.
    // Pouze lomitku "/" nerozumi, proto prevedeni root route path "/" na "".
    rootRoute.path =
      typeof rootRoute.path === 'object'
        ? Object.fromEntries(Object.entries(rootRoute.path).map(([key]) => [key, '']))
        : '';
  }

  let isMapRoute = routes.some((route) => route.component === 'Map');
  if (!isMapRoute && showMap && !(rootRoute && routes.every((route) => route.showMap))) {
    isMapRoute = true;
    routes.push({
      id: 'map',
      auth,
      hasBasePermission,
      path: {
        cs: rootRoute || isAboutRouteConfigured ? '/mapa' : '',
        en: rootRoute || isAboutRouteConfigured ? '/map' : '',
      },
      component: 'Map',
      showMap: true,
    });
  }

  const panoramaField = (
    config.info?.services.find((service) => service.type === 'WFS') as WFSInfoState
  )?.properties?.fields?.find((field) => field.type === 'panorama');
  if (isMapRoute && panoramaField) {
    routes.push({
      id: 'panorama',
      auth,
      hasBasePermission,
      path: '/panorama',
      component: 'Panorama',
      defaultSize: 'large',
      showMap: true,
    });
  }

  if (
    isMapRoute &&
    !routes.find((route) => route.component === 'MapInfo') &&
    (config.info?.services.length || config.info?.coordsInfo)
  ) {
    routes.push({
      id: 'info',
      auth,
      hasBasePermission,
      path: {
        cs: '/mapa/:geom',
        en: '/map/:geom',
      },
      component: 'MapInfo',
      defaultSize: 'small',
      showMap: true,
    });
  }

  if (!rootRoute && isAboutRouteConfigured) {
    routes.push({
      id: DICTIONARY_PAGES.ABOUT,
      auth,
      hasBasePermission,
      path: {
        cs: '',
        en: '',
      },
      component: 'Dictionary',
      showMap,
    });
  }

  const isOtherRouteDoNotAvailible = !routes.some(
    (route) => (!route.auth || authState.isLoggedIn) && route.hasBasePermission,
  );
  if (isOtherRouteDoNotAvailible && (!auth || authState.isLoggedIn) && isBlankRouteConfigured) {
    const rootRoute = getRootRoute(routes, lang);
    const indexRoute = routes.findIndex((route) => route === rootRoute);
    routes.splice(indexRoute, 1);

    routes.push({
      id: DICTIONARY_PAGES.BLANK,
      auth,
      hasBasePermission,
      path: {
        cs: '',
        en: '',
      },
      component: 'Dictionary',
      showMap,
    });
  }

  routes.push({
    id: 'user-profile',
    auth: true,
    hasBasePermission,
    path: {
      cs: '/profil-uzivatele',
      en: '/user-profile',
    },
    component: 'UserProfile',
    showMap: false,
  });

  routes.push({
    id: 'IDE',
    path: '/IDE',
    component: 'IDE',
    auth: false,
    hasBasePermission: true,
    showMap: false,
  });

  return routes;
};
